import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  User,
  PageContent,
  openNotificationWithIcon,
  Segment,
  useUserinfo,
  PermissionGuard,
  Flex,
} from '@mss/mss-component-library';
import { Collapse, notification } from 'antd';
import Api from '../../../services/Api';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  PermissionColumn,
  permissions,
  PermissionSetOption,
  UserPermissionsAPI,
} from '../../../util/permissionsForEdit';
import { useTranslation } from 'react-i18next';
import { Cell, RowHeader } from 'jsx/authorization/components/Table';
import PermissionRow, {
  CHECKBOX_COLUMN_WIDTH,
} from 'jsx/authorization/components/PermissionRow';
import PageContainer from 'jsx/authorization/components/PageContainer';
import { AppContext } from 'jsx';
import { useLicensesQuery } from '../../../../asset/services/queries';

type Props = {
  checkedList: (string | undefined)[];
  setCheckedList: Dispatch<SetStateAction<(string | undefined)[]>>;
  user: User;
  userPermissions: UserPermissionsAPI[];
  fetchedUserPermissions: any;
  fetchUserPermissions: any;
};

export default function Permissions({
  checkedList,
  setCheckedList,
  user,
  userPermissions,
  fetchedUserPermissions,
  fetchUserPermissions,
}: Props) {
  const [hasPermission] = useUserinfo();
  const [t] = useTranslation(['authorization', 'common']);
  const { allPermissionsMap, permissionsWithView } = useContext(AppContext);
  const { data: license } = useLicensesQuery();
  const [isFetching, setIsFetching] = useState<boolean>(false);

  let optionsWithViewPermission: PermissionSetOption[] = [];
  permissionsWithView.forEach(option => {
    const found = checkedList.find(item => item === option.codename);
    if (found && option.view) {
      optionsWithViewPermission.push(option);
    }
  });

  const licensedPermissionsCodenames = permissionsWithView
    .filter(
      perm =>
        !!perm.license_codename && license && perm.license_codename in license
    )
    .map(el => el.codename);

  const handleChange = async (
    e: CheckboxChangeEvent,
    userPermission: UserPermissionsAPI | undefined,
    userPermissions: UserPermissionsAPI[],
    permissionId: number,
    permissionObject: PermissionSetOption
  ) => {
    setIsFetching(true);
    const enable = e.target.checked;

    // Add view permission on Create / Update / Delete
    let viewPermissionId: number[] = [];

    if (permissionObject.view) {
      if (Array.isArray(permissionObject.view)) {
        permissionObject.view.forEach((viewCodename: string) => {
          const permCode = allPermissionsMap.get(viewCodename);
          if (permCode) viewPermissionId.push(permCode);
        });
      } else {
        const permCode = allPermissionsMap.get(permissionObject.view);
        if (permCode) viewPermissionId = [permCode];
      }
    }

    const resp = await Api.user.permission.toggle(
      {
        permission:
          enable && viewPermissionId.length
            ? [permissionId, ...viewPermissionId]
            : [permissionId],
        user: user.id,
      },
      enable ? undefined : userPermission?.user_permission_id
    );

    if (resp.status >= 200 && resp.status < 300) {
      fetchUserPermissions();
      openNotificationWithIcon(
        enable ? notification.success : notification.warning,
        enable ? t(`Permission added`) : t('Permission removed'),
        '',
        5
      );
      setIsFetching(false);
    } else {
      openNotificationWithIcon(
        notification.error,
        t('Changing permissions failed'),
        t(
          'The permission could not be changed for this user. Please try again later.'
        )
      );
      setIsFetching(false);
    }
  };

  const handleCheckAll = async (
    e: CheckboxChangeEvent,
    permissions: string[]
  ) => {
    setIsFetching(true);
    const enable = e.target.checked;

    // View permissions to autoselect when 'Check All'
    let viewPermissions: string[] = [];
    permissions.forEach(codename => {
      const foundPermObj = permissionsWithView.find(
        permObj => permObj.codename === codename
      );
      if (foundPermObj && foundPermObj.view) {
        const viewPermission = Array.isArray(foundPermObj.view)
          ? [...foundPermObj.view]
          : [foundPermObj.view];
        viewPermissions.push(...viewPermission);
      }
    });

    const permissionsCodesWithView = [
      ...permissions,
      ...viewPermissions,
    ].reduce((acc: number[], codename) => {
      const permission = allPermissionsMap.get(codename);
      if (permission) return [...acc, permission];
      return acc;
    }, []);

    let response: Response;
    if (enable) {
      setCheckedList([...checkedList, ...permissions, ...viewPermissions]);
      response = await Api.user.permission.toggle({
        permission: permissionsCodesWithView,
        user: user.id,
      });
    } else {
      const permissionsToDisable = permissions.reduce(
        (acc: { names: string[]; codes: number[] }, codename) => {
          if (
            codename.includes('view_') &&
            !codename.includes('view_center_')
          ) {
            const hasRelatedPermission = checkedList
              .filter(x => x && !x.includes('view_'))
              .some(x => x?.includes(codename.replace('view_', '')));
            if (hasRelatedPermission) return acc;
          }
          const permissionCode = allPermissionsMap.get(codename);
          if (permissionCode)
            return {
              names: [...acc.names, codename],
              codes: [...acc.codes, permissionCode],
            };
          return acc;
        },
        { names: [], codes: [] }
      );

      setCheckedList(() =>
        checkedList.filter(each => !permissionsToDisable.names.includes(each!))
      );
      if (permissionsToDisable.codes.length === 0) {
        setIsFetching(false);
        openNotificationWithIcon(
          notification.info,
          t('No permissions to remove'),
          '',
          5
        );
        return;
      }
      response = await Api.user.permission.toggle(
        {
          permission: permissionsToDisable.codes,
          user: user.id,
        },
        user.id
      );
    }

    if (response.status >= 200 && response.status < 300) {
      // fetchUserPermissions();
      openNotificationWithIcon(
        enable ? notification.success : notification.warning,
        enable ? t(`Permission added`) : t('Permission removed'),
        '',
        5
      );
      setIsFetching(false);
    } else {
      openNotificationWithIcon(
        notification.error,
        t('Changing permissions failed'),
        t(
          'The permission could not be changed for this user. Please try again later.'
        )
      );
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!!user.id) fetchUserPermissions();
    // eslint-disable-next-line
  }, [user.id]);

  const hasIds = allPermissionsMap.size > 0 && userPermissions != null;
  const isDisabled =
    !hasIds ||
    fetchedUserPermissions.loading ||
    !hasPermission(['change_userpermission']);

  const defaultActiveKey = permissions(t).map(p => p.label);

  return (
    <PermissionGuard
      redirect
      codename={['change_userpermission', 'view_userpermission']}
    >
      <PageContainer>
        <PageContent title="Permissions">
          {!!user.id && hasIds ? (
            <Collapse
              defaultActiveKey={defaultActiveKey}
              expandIconPosition="end"
            >
              {permissions(t).map(permissionsSet => {
                return (
                  <Collapse.Panel
                    header={permissionsSet.label}
                    key={permissionsSet.label}
                    data-ta={`permissionSet-${permissionsSet.label}`}
                  >
                    <Segment>
                      <RowHeader>
                        <Cell style={{ paddingLeft: 16 }}>
                          {t('Permission')}
                        </Cell>
                        {permissionsSet.columns.map(
                          (column: PermissionColumn) => {
                            const columnPermissions = column.column_permissions;

                            const checked = columnPermissions?.every(codename =>
                              checkedList.includes(codename)
                            );

                            const indeterminate =
                              checkedList.some(codename =>
                                columnPermissions?.includes(codename!)
                              ) && !checked;

                            // If any permission in column is not included in license - disable check all
                            const checkAllLicenseDisabled =
                              columnPermissions?.some(
                                codename =>
                                  !licensedPermissionsCodenames.includes(
                                    codename
                                  )
                              );
                            return (
                              <Cell
                                data-ta="permissionTypeHeader"
                                key={column.label}
                                width={CHECKBOX_COLUMN_WIDTH}
                                style={{
                                  justifyContent: 'center',
                                  textAlign: 'center',
                                }}
                              >
                                <Flex v="center" h="center">
                                  {column.label}
                                  {columnPermissions && (
                                    <Checkbox
                                      className="ml-1"
                                      checked={checked}
                                      indeterminate={indeterminate}
                                      disabled={
                                        isFetching ||
                                        isDisabled ||
                                        checkAllLicenseDisabled
                                      }
                                      onChange={e =>
                                        handleCheckAll(
                                          e,
                                          column.column_permissions
                                        )
                                      }
                                    />
                                  )}
                                </Flex>
                              </Cell>
                            );
                          }
                        )}
                      </RowHeader>
                      {!!license &&
                        permissionsSet.permissions.map(perSet => (
                          <PermissionRow
                            checkedList={checkedList}
                            setCheckedList={setCheckedList}
                            optionsWithViewPermission={
                              optionsWithViewPermission
                            }
                            key={perSet.label}
                            perSet={perSet}
                            userPermissions={userPermissions}
                            disabled={isFetching || isDisabled}
                            license={license}
                            handleChange={handleChange}
                          />
                        ))}
                    </Segment>
                  </Collapse.Panel>
                );
              })}
            </Collapse>
          ) : null}
        </PageContent>
      </PageContainer>
    </PermissionGuard>
  );
}
